import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, TrendingUp, ArrowRight, CheckCircle, ShieldCheck, Clock } from 'lucide-react';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Performance',
      description: 'Un service de qualité et des rendez-vous qualifiés',
    },
    {
      icon: Users,
      title: 'Proximité',
      description: 'Une relation de confiance et un accompagnement personnalisé.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Des standards élevés dans chaque mission réalisée.',
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'Des méthodes modernes et des outils à la pointe.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#ECFDF5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Pourquoi 0to1 ?</span>
              <h1 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6" data-testid="about-title">
                Le partenaire pour vous accompagner dans la transformation de votre <span className="text-[#10B981]">prospection</span>
              </h1>
              <p className="text-lg text-[#1E3A5F] leading-relaxed mb-8">
                0to1 est née d'une conviction simple : la prospection commerciale doit être humaine, personnalisée et génératrice de vraies opportunités.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="about-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#10B981] text-white font-semibold rounded-full px-6 py-3 hover:bg-[#0D6B5E] transition-all duration-200"
                >
                  Planifier un échange
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#10B981] text-[#10B981] font-semibold rounded-full px-6 py-3 hover:bg-[#E8F5F3] transition-all duration-200"
                >
                  Mes expertises
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_prospection-pro/artifacts/9mfjza8b_e09838b2-3e26-4c25-a108-2c42e5956572.webp"
                  alt="Jehan, fondateur de 0to1"
                  className="w-full h-[400px] object-cover object-top"
                />
              </div>
              <p className="text-center text-[#1E3A5F] mt-4 text-sm">Jehan, fondateur de 0to1</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 border-b border-gray-100" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">Ma mission</h2>
              <p className="text-[#1E3A5F] text-lg leading-relaxed">
                Permettre à chaque entreprise d'atteindre son plein potentiel commercial grâce à une prospection de qualité, des méthodes éprouvées et un accompagnement personnalisé.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">Ma vision</h2>
              <p className="text-[#1E3A5F] text-lg leading-relaxed">
                Devenir le partenaire de référence des entreprises B2B en matière de développement commercial, en combinant expertise humaine et innovation technologique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agency Section */}
      <section className="py-20" data-testid="why-agency-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Les avantages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">
              Pourquoi externaliser votre prospection ?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Scenario 1 */}
            <div className="bg-[#ECFDF5] border border-gray-100 rounded-2xl p-8 hover:border-[#10B981] transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#E8F5F3] flex items-center justify-center mb-6">
                <ShieldCheck size={28} className="text-[#10B981]" />
              </div>
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-4">Réduire le risque financier</h3>
              <p className="text-[#1E3A5F] leading-relaxed mb-4">
                Beaucoup d'entreprises se lancent tête baissée dans le recrutement d'équipes commerciales sans avoir validé en amont leur capacité à générer des rendez-vous, ni défini clairement leur <span className="text-[#10B981] font-medium">ICP</span> (Ideal Customer Profile) ou leur <span className="text-[#10B981] font-medium">MVP</span> (Minimum Viable Product).
              </p>
              <p className="text-[#1E3A5F] leading-relaxed">
                Cette précipitation peut coûter très cher. Externaliser permet de <span className="text- font-medium">réduire ce risque financier</span> en confiant à moindre coût cette phase de test pour valider son ICP et MVP, puis se lancer sur une croissance contrôlée car éprouvée en amont.
              </p>
            </div>

            {/* Scenario 2 */}
            <div className="bg-[#ECFDF5] border border-gray-100 rounded-2xl p-8 hover:border-[#10B981] transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#E8F5F3] flex items-center justify-center mb-6">
                <Clock size={28} className="text-[#10B981]" />
              </div>
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-4">Répondre à un besoin temporaire</h3>
              <p className="text-[#1E3A5F] leading-relaxed mb-4">
                D'autres entreprises ont un besoin temporaire lié à une <span className="text-[#10B981] font-medium">forte activité</span> ou une <span className="text-[#10B981] font-medium">croissance non anticipée</span>.
              </p>
              <p className="text-[#1E3A5F] leading-relaxed">
                Cela peut également être suite à un <span className="text-[#10B981] font-medium">départ non prévu</span> pour pallier au délai de recrutement d'une nouvelle personne. Un partenaire commercial externe offre la flexibilité nécessaire pour maintenir votre activité sans interruption.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#ECFDF5]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Mes valeurs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">
              Ce qui m'anime au quotidien
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg"
              >
                <div className="w-14 h-14 rounded-xl bg-[#10B981] flex items-center justify-center mb-6">
                  <value.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{value.title}</h3>
                <p className="text-[#1E3A5F]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Mon expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">
              Mes domaines de prédilection
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Secteurs d'activité */}
            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6">Secteurs d'activité de prédilection</h3>
              <div className="flex flex-wrap gap-3">
                {['Fintech', 'HR Tech', 'SaaS B2B', 'Cybersécurité'].map((sector, index) => (
                  <span
                    key={index}
                    className="bg-[#10B981] text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-md"
                  >
                    {sector}
                  </span>
                ))}
              </div>
            </div>

            {/* Interlocuteurs */}
            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6">Interlocuteurs de prédilection</h3>
              <div className="flex flex-wrap gap-3">
                {['DAF', 'DRH', 'Directeur paie', 'Head of Sales', 'CTO', 'C&B'].map((role, index) => (
                  <span
                    key={index}
                    className="bg-[#10B981] text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-md"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#ECFDF5]" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Pourquoi 0to1</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-8">
              Une approche différente de la prospection
            </h2>
            <ul className="space-y-6">
              {[
                'Plus de 7 années d\'expérience terrain',
                'Un interlocuteur unique et disponible',
                'Une méthodologie éprouvée et adaptable',
                'Des résultats mesurables et transparents',
                'Des outils modernes et performants',
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-4">
                  <CheckCircle size={24} className="text-[#10B981] flex-shrink-0" />
                  <span className="text-[#1E3A5F] text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
